import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { compose } from 'recompose';
import { Button } from 'antd';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import ModalDlg from 'components/Basic/ModalDlg';
import LinkModal from 'components/Basic/LinkModal';
import cardImg from 'assets/img/card.svg';
import trackImg from 'assets/img/track.svg';
import { getCurrency } from 'utilities/common';

const esLocale = require('moment/locale/es');

function OrderDetailModal({ intl, orderDetail, isOpenModal, onCloseModal }) {
  const [isLinkModalVisible, setLinkModalVisible] = useState(false);

  const orderStates = {
    RV: intl.formatMessage({ id: 'RV' }),
    IP: intl.formatMessage({ id: 'IP' }),
    AS: intl.formatMessage({ id: 'AS' }),
    OW: intl.formatMessage({ id: 'OW' }),
    AD: intl.formatMessage({ id: 'AD' }),
    CL: intl.formatMessage({ id: 'CL' }),
    RS: intl.formatMessage({ id: 'RS' }),
    DD: intl.formatMessage({ id: 'DD' }),
    CD: intl.formatMessage({ id: 'CD' }),
    ERR: intl.formatMessage({ id: 'ERR' }),
  };
  if (!orderDetail) {
    return null;
  }
  const OrderDetail = () => {
    return (
      <div className="order_details">
        <div className="group_name">
          <FormattedMessage id="Order details" /> <div className="divider" />
        </div>
        <div className="order_number_elapsed">
          <div>
            <p className="title">
              <FormattedMessage id="Order number" />
            </p>
            <p className="description">#{orderDetail.order || orderDetail.id}</p>
          </div>
          <div>
            <p className="title">
              <FormattedMessage id="Time elapsed" />
            </p>
            <p className="description">En espera</p>
          </div>
        </div>
        <div className="delivery_address">
          <p className="title">
            <FormattedMessage id="Delivery Address" />
          </p>
          <p className="description">{orderDetail.deliveryAddress.name || ''}</p>
          <a
            className="sub_description"
            href={`https://www.google.com/maps?z=12&t=m&q=loc:${orderDetail.deliveryAddress.lat}+${orderDetail.deliveryAddress.long}`}
            // eslint-disable-next-line react/jsx-no-target-blank
            target="_blank"
          >
            {orderDetail.deliveryAddress.address || ''}
          </a>
        </div>
        <div className="delivery_time">
          <p className="title">
            <FormattedMessage id="Delivery day and time" />
          </p>
          <p className="description">
            {orderDetail.dueTo
              ? `${moment(orderDetail.dueTo)
                  .locale('es', esLocale)
                  .tz('America/Guatemala')
                  .format('LLLL')} horas`
              : ''}
          </p>
        </div>
        <div className="order_number_elapsed">
          <div>
            <p className="title">
              <FormattedMessage id="Who to ask for?" />
            </p>
            <p className="description">{`${orderDetail.customer.firstName} ${orderDetail.customer.lastName}`}</p>
          </div>
          {orderDetail.agent && (
            <div>
              <p className="title">Agente</p>
              <p className="description">{`${orderDetail.agent.firstName} ${orderDetail.agent.lastName}`}</p>
            </div>
          )}
        </div>
      </div>
    );
  };
  const OrderItems = () => {
    return (
      <div className="order_items">
        <div className="group_name">
          <FormattedMessage id="Order elements" /> <div className="divider" />
        </div>
        <div className="item_list">
          {orderDetail.contents &&
            orderDetail.contents.map((order, index) => (
              <div className="item" key={index}>
                <p className="name" dangerouslySetInnerHTML={{ __html: order.item.name }} />
                {order.options.map(({ ingredients = [], aggregates = [], option, price }, _index) => (
                  <div key={_index}>
                    <div>{option.name + (!price ? '' : ` +${getCurrency().symbol}${(price / 100).toFixed(2)}`)}</div>
                    {ingredients
                      .filter(({ quantity }) => quantity === 0 || quantity > 1)
                      .map(({ price: _price, quantity, recipe }) => (
                        <div key={recipe.id}>
                          {`${quantity === 0 ? 'Sin' : 'Extra'} ${recipe.description || ''}${
                            quantity === 0 || !_price
                              ? ''
                              : ` +${getCurrency().symbol}${((_price / 100) * (quantity - 1)).toFixed(2)}`
                          }`}
                        </div>
                      ))}
                    {aggregates
                      .filter(({ quantity }) => quantity > 0)
                      .map(({ price: _price, quantity, recipe }) => (
                        <div>
                          {`Extra: ${recipe.description || ''}${
                            !_price ? '' : ` +${getCurrency().symbol}${((_price / 100) * quantity).toFixed(2)}`
                          }`}
                        </div>
                      ))}
                  </div>
                ))}
                <div>
                  {order.ingredients
                    .filter(item => {
                      return item.quantity === 0 || item.quantity > 1;
                    })
                    .map(({ recipe, price, quantity }) => (
                      <div className="additional" key={Math.random()}>
                        {`${quantity === 0 ? 'Sin:' : 'Extra:'} ${recipe.description}${
                          quantity === 0 || !price
                            ? ''
                            : ` +${getCurrency().symbol}${((price / 100) * (quantity - 1)).toFixed(2)}`
                        }`}
                      </div>
                    ))}
                  {order.aggregates
                    .filter(({ quantity }) => quantity > 0)
                    .map(({ recipe, price, quantity }) => (
                      <div className="additional" key={Math.random()}>
                        {`Extra: ${recipe.description} ${
                          !price ? '' : `${getCurrency().symbol}${((price / 100) * quantity).toFixed(2)}`
                        }`}
                      </div>
                    ))}
                </div>
                <p className="amount">
                  {getCurrency().symbol}
                  {parseFloat(order.price / 100).toFixed(2)} x {order.quantity} = {getCurrency().symbol}
                  {parseFloat((order.price * order.quantity) / 100).toFixed(2)}
                </p>
              </div>
            ))}
        </div>
        <div className="order_total">
          <p className="title">
            <FormattedMessage id="Order total" />:
          </p>
          <p className="amount">
            {getCurrency().symbol}
            {parseFloat(orderDetail.paymentAmount / 100).toFixed(2) || ''}
          </p>
        </div>
      </div>
    );
  };
  return (
    <ModalDlg
      className="order_detail_modal"
      handleCancel={() => onCloseModal()}
      top={60}
      visible={isOpenModal}
      width={413}
    >
      {orderDetail && (
        <>
          <div className="detail_content">
            <div className="detail_header">
              <p>
                <FormattedMessage id="Order" /> #{orderDetail.order || orderDetail.id}
              </p>
              <Button className={`${orderDetail.state}`}>{orderStates[orderDetail.state]}</Button>
              {['OW', 'AD'].includes(orderDetail.state) && (
                <>
                  <div className="track_order" onClick={() => setLinkModalVisible(true)}>
                    <img alt="track order" src={trackImg} />
                  </div>
                  <LinkModal
                    onClose={() => setLinkModalVisible(false)}
                    title="Rastreo de pedido"
                    url={`https://products.loginextsolutions.com/track/#/order?ordno=${orderDetail.order}&aid=2c55ff9d-38f3-4917-b4dc-150a0dec6153&type=ADMIN`}
                    visible={isLinkModalVisible}
                  />
                </>
              )}
            </div>
            <OrderDetail />
            <OrderItems />
          </div>
          {orderDetail.paymentMethod.type === 'C' && (
            <div className="detail_footer">
              <p className="pay_way_title">
                <FormattedMessage id="Way to pay" />
              </p>
              <div className="card_info">
                <img alt="card" src={cardImg} />
                <div>
                  <p>
                    <FormattedMessage id="Paid by credit or debit card" />
                  </p>
                  <p>
                    <FormattedMessage id="ending in" /> {orderDetail.paymentMethod.number}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </ModalDlg>
  );
}

OrderDetailModal.propTypes = {
  intl: intlShape.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  isOpenModal: PropTypes.bool,
  orderDetail: PropTypes.object,
};

OrderDetailModal.defaultProps = {
  isOpenModal: false,
  orderDetail: null,
};

export default compose(injectIntl)(OrderDetailModal);
